import { LocalizedLink as Link } from "@/components/routing/LocalizedLink";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PublicShell } from "@/components/layouts/PublicShell";
import { Seo } from "@/components/seo/Seo";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import { useLocalizedField } from "@/hooks/useLocalizedField";
import { organizationFullLd } from "@/lib/seo/structured-data";
import type { Locale } from "@/lib/seo/hreflang";
import { CASE_STUDIES } from "@/content/case-studies";
import { type } from "@/lib/typography";
import { EcosystemContent, t as tt, type Lang } from "@/content/ecosystem";
import { ArrowRight, MapPin, GraduationCap, Compass, Users, Users2, Briefcase, Boxes, Sparkles, TrendingUp, Award, Globe2, Map, Rocket, BadgeCheck, ShieldCheck } from "lucide-react";
import { HeroMesh } from "@/components/marketing/HeroMesh";
import { EcosystemCard } from "@/components/home/EcosystemCard";
import { BootcampCover } from "@/components/bootcamp/BootcampCover";
import { getIntroVideoForSlug } from "@/content/bootcamp-video-map";
import { bootcamps as localBootcamps } from "@/content/bootcamps";
import { SmartImage } from "@/components/SmartImage";
import { CaseStudyCoverArt } from "@/components/marketing/CaseStudyCoverArt";
import { PriceBadge } from "@/components/pricing/PriceBadge";
import { Price } from "@/components/pricing/Price";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { SectionHeader } from "@/components/ui/section-header";
import { StatCard } from "@/components/ui/stat-card";
import { IntentCard, type IntentArt } from "@/components/marketing/IntentCard";
import { FeaturedProvidersRail } from "@/components/home/FeaturedProvidersRail";
import { BusinessValidatorWidget } from "@/components/landing/BusinessValidatorWidget";
import { Reveal } from "@/components/motion/Reveal";
import { StaggerGrid } from "@/components/motion/StaggerGrid";
import { HoverLift } from "@/components/motion/HoverLift";

const ICONS = { GraduationCap, Compass, Users, Briefcase, Boxes, Users2 } as const;

export default function Landing() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language?.slice(0, 2) || "en") as Lang;
  const pick = useLocalizedField();
  const [bootcamps, setBootcamps] = useState<any[]>([]);
  const [founderAnnual, setFounderAnnual] = useState(false);
  const founderMonthlyEur = 19;
  const founderAnnualEur = 179;
  const founderSavingsPct = Math.round((1 - founderAnnualEur / (founderMonthlyEur * 12)) * 100);
  const [loading, setLoading] = useState(true);
  const [proofs, setProofs] = useState<any[]>([]);

  const faqItems = [
    { q: t("marketing.faq.q1"), a: t("marketing.faq.a1") },
    { q: t("marketing.faq.q2"), a: t("marketing.faq.a2") },
    { q: t("marketing.faq.q3"), a: t("marketing.faq.a3") },
    { q: t("marketing.faq.q4"), a: t("marketing.faq.a4") },
    { q: t("marketing.faq.q5"), a: t("marketing.faq.a5") },
  ];
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map((it) => ({
      "@type": "Question",
      name: it.q,
      acceptedAnswer: { "@type": "Answer", text: it.a },
    })),
  };

  useEffect(() => {
    supabase
      .from("case_studies")
      .select("slug, founder_name, company, city, cover_image_url, metrics")
      .eq("published", true)
      .limit(8)
      .then(({ data }) => setProofs(data ?? []));
  }, []);

  useEffect(() => {
    supabase
      .from("bootcamps")
      .select("id, slug, title_en, title_es, title_fr, title_pt, subtitle_en, subtitle_es, subtitle_fr, subtitle_pt, price_eur, level, duration_weeks, cover_image_url")
      .eq("published", true)
      .limit(8)
      .then(({ data, error }) => {
        if (error) console.error("Featured bootcamps query failed:", error);
        // Hydrate flagship flag from local content (DB has no is_flagship column),
        // then pin flagship-first.
        const localBySlug: globalThis.Map<string, typeof localBootcamps[number]> = new globalThis.Map(
          localBootcamps.map((bc) => [bc.slug, bc] as const),
        );
        const hydrated = (data ?? []).map((b) => ({
          ...b,
          is_flagship: localBySlug.get(b.slug)?.is_flagship === true,
        }));
        hydrated.sort(
          (a, b) => Number(b.is_flagship === true) - Number(a.is_flagship === true),
        );
        setBootcamps(hydrated);
        setLoading(false);
      });
  }, []);

  // Single source of truth: derive city band from CASE_STUDIES so quote
  // and byline can never drift from the linked case study.
  const cities = (["lagos-fintech", "dakar-gtm", "luanda-saas"] as const)
    .map((slug) => CASE_STUDIES.find((c) => c.slug === slug))
    .filter((c): c is NonNullable<typeof c> => Boolean(c))
    .map((c) => {
      const body = c.body[lang as "en" | "fr"] ?? c.body.en;
      return {
        name: c.company,
        country: `${c.city}, ${c.country}`,
        quote: body.quote,
        slug: c.slug,
      };
    });

  const teaserTiers = [
    {
      key: "free",
      name: t("marketing.pricing.tierFree", { defaultValue: "Free" }),
      sku: "tier-free",
      fallbackEur: 0,
      suffix: "",
      desc: t("marketing.pricing.tierFreeDesc", { defaultValue: "Diagnostic, AI mentor (5 msg/day), 3 free downloads, sample lessons, community read-only." }),
    },
    {
      key: "founder",
      name: t("marketing.pricing.tierFounder", { defaultValue: "Founder Pass" }),
      sku: "founder-pass-monthly",
      fallbackEur: 19,
      suffix: t("marketing.pricing.perMonth", { defaultValue: "/mo" }),
      desc: t("marketing.pricing.tierFounderDesc", { defaultValue: "Unlimited access to KORA (AI mentor) and Velyra (AI strategist), unlimited downloads, full community, recordings, monthly office hours, certificates." }),
      highlight: true,
      annualEur: 179,
    },
    {
      key: "bootcamp",
      name: t("marketing.pricing.tierBootcampNew", { defaultValue: "Bootcamps" }),
      sku: "bootcamp-beginner",
      fallbackEur: 99,
      suffix: t("marketing.pricing.perCohort", { defaultValue: "/ cohort" }),
      desc: t("marketing.pricing.tierBootcampNewDesc", { defaultValue: "4-week cohorts. Beginner / Intermediate / Advanced. Includes Founder Pass for the cohort + 30 days." }),
      fromPrefix: true,
    },
    {
      key: "coaching",
      name: t("marketing.pricing.tierCoachingNew", { defaultValue: "Coaching" }),
      sku: "coaching-4h",
      fallbackEur: 299,
      suffix: t("marketing.pricing.perMonth", { defaultValue: "/mo" }),
      desc: t("marketing.pricing.tierCoachingNewDesc", { defaultValue: "4h, 8h or 12h per month with an operator-mentor. Founder Pass included." }),
      fromPrefix: true,
    },
  ];

  // Theme-reactive gradient using HSL design tokens
  const heroGradient =
    "radial-gradient(1200px 600px at 15% -10%, hsl(var(--primary) / 0.18) 0%, transparent 55%), " +
    "radial-gradient(900px 500px at 95% 30%, hsl(var(--accent) / 0.12) 0%, transparent 60%), " +
    "linear-gradient(180deg, hsl(var(--background)) 0%, hsl(var(--muted) / 0.4) 100%)";

  return (
    <PublicShell>
      <Seo
        title={t("home.meta.title", { defaultValue: t("marketing.landing.seoTitle") })}
        description={t("home.meta.description", { defaultValue: t("marketing.landing.seoDescription") })}
        jsonLd={[organizationFullLd((lang as Locale) ?? "en"), faqJsonLd]}
      />

      {/* Hero + Pillars — single shared gradient block */}
      <div className="relative overflow-hidden border-b border-border" style={{ background: heroGradient }}>
        <HeroMesh />

        {/* Hero — Editorial Monumental */}
        <section className="container relative py-20 md:py-28 lg:py-32">
          {/* Ambient editorial glows (additive to HeroMesh, low opacity) */}
          <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
            <div className="absolute -left-24 -top-24 h-96 w-96 rounded-full bg-primary/20 blur-[120px]" />
            <div className="absolute -right-24 top-1/2 h-[500px] w-[500px] rounded-full bg-primary/10 blur-[150px]" />
          </div>

          <div className="relative grid items-center gap-14 lg:grid-cols-[1.1fr_0.9fr]">
            <div className="space-y-8">
              {/* Eyebrow (copy already includes glyph) */}
              <p className="font-mono-jb text-[11px] font-bold uppercase tracking-[0.22em] text-accent">
                {tt(EcosystemContent.hero.eyebrow, lang)}
              </p>

              {/* Single monumental headline */}
              <div className="space-y-4">
                <h1 className="max-w-[16ch] text-balance text-6xl font-extrabold leading-[0.95] tracking-[-0.04em] text-foreground lg:text-[5.5rem]">
                  {tt(EcosystemContent.hero.tagline, lang)}
                </h1>
                <p className="text-xl font-semibold tracking-tight text-foreground/90">
                  {tt(EcosystemContent.hero.h1, lang)}
                </p>
              </div>

              {/* Lede */}
              <p className="max-w-xl text-pretty text-lg leading-relaxed text-muted-foreground">
                {tt(EcosystemContent.hero.sub, lang)}
              </p>

              {/* CTAs */}
              <div className="flex flex-wrap items-center gap-4 pt-2">
                <Button asChild variant="brand" size="xl" className="group">
                  <Link to="/signup">
                    {tt(EcosystemContent.hero.ctaPrimary, lang)}
                    <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                </Button>
                <Button asChild variant="brandOutline" size="xl">
                  <Link to="/catalog">{tt(EcosystemContent.hero.ctaSecondary, lang)}</Link>
                </Button>
                <Link
                  to="/contact"
                  className="ml-1 border-b border-foreground/20 pb-0.5 text-sm font-semibold text-muted-foreground transition-colors hover:text-foreground"
                >
                  {tt(EcosystemContent.hero.ctaTertiary, lang)}
                </Link>
              </div>

              {/* Trust microcopy */}
              <p className="border-l-2 border-primary pl-4 text-[11px] font-bold uppercase tracking-[0.1em] text-muted-foreground">
                {tt(EcosystemContent.hero.usedBy, lang)}
              </p>
            </div>

            {/* Ecosystem panel — editorial glass frame */}
            <div className="hidden lg:block">
              <div className="relative rounded-3xl border border-foreground/5 bg-foreground/[0.02] p-2 shadow-2xl backdrop-blur-3xl">
                <EcosystemCard />
              </div>
            </div>
          </div>
        </section>

        {/* Business Validator — lead magnet widget (right after hero) */}
        <BusinessValidatorWidget />

        {/* Intent Router */}
        <section className="container relative pb-16 md:pb-20">
          <SectionHeader
            className="mb-8"
            eyebrow={t("marketing.landing.intentRouter.eyebrow", { defaultValue: "Start here" })}
            title={t("marketing.landing.intentRouter.title", { defaultValue: "What are you trying to do this quarter?" })}
            lede={t("marketing.landing.intentRouter.lede", { defaultValue: "Pick a goal — we route you to the right path in seconds." })}
          />
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {([
              { key: "customers", art: "customers", to: "/diagnostic?blocker=customers", label: t("marketing.landing.intentRouter.customers", { defaultValue: "Find my first 100 customers" }), description: t("marketing.landing.intentRouter.customersDesc", { defaultValue: "Activate GTM playbooks and warm intros." }) },
              { key: "fundraising", art: "fundraising", to: "/diagnostic?blocker=fundraising", label: t("marketing.landing.intentRouter.fundraising", { defaultValue: "Raise pre-seed or seed" }), description: t("marketing.landing.intentRouter.fundraisingDesc", { defaultValue: "Investor-ready story, deck, and intros." }) },
              { key: "team", art: "team", to: "/diagnostic?blocker=team", label: t("marketing.landing.intentRouter.team", { defaultValue: "Hire and ship faster" }), description: t("marketing.landing.intentRouter.teamDesc", { defaultValue: "Fractional operators and proven hires." }) },
              { key: "catalog", art: "catalog", to: "/catalog", label: t("marketing.landing.intentRouter.catalog", { defaultValue: "Show me everything" }), description: t("marketing.landing.intentRouter.catalogDesc", { defaultValue: "Browse the full bootcamp and services catalog." }) },
            ] as Array<{ key: string; art: IntentArt; to: string; label: string; description: string }>).map((card) => (
              <IntentCard
                key={card.key}
                to={card.to}
                label={card.label}
                description={card.description}
                art={card.art}
                cta={t("common.explore", { defaultValue: "Explore" })}
              />
            ))}
          </div>
        </section>

        {/* Sprint 3.1 — Spotlight rail */}
        <FeaturedProvidersRail />

        {/* Platform promise strip — what we offer (no fabricated stats) */}
        <section className="container relative pb-16 md:pb-20">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              label={t("marketing.landing.promises.scopeLabel", { defaultValue: "What we do" })}
              value={t("marketing.landing.promises.scopeValue", { defaultValue: "Bootcamps + on-demand experts" })}
              hint={t("marketing.landing.promises.scopeHint", { defaultValue: "One platform from idea to first revenue." })}
              icon={<Compass className="h-4 w-4" />}
              status={{ tone: "live", label: t("marketing.landing.promises.statusLive", { defaultValue: "Live" }) }}
            />
            <StatCard
              label={t("marketing.landing.promises.aiLabel", { defaultValue: "Built for founders" })}
              value={t("marketing.landing.promises.aiValue", { defaultValue: "AI co-pilot, human review" })}
              hint={t("marketing.landing.promises.aiHint", { defaultValue: "Kora guides you. Vetted operators close the gap." })}
              icon={<Sparkles className="h-4 w-4" />}
              status={{ tone: "new", label: t("marketing.landing.promises.statusNew", { defaultValue: "New" }) }}
            />
            <StatCard
              label={t("marketing.landing.promises.reachLabel", { defaultValue: "Who we serve" })}
              value={t("marketing.landing.promises.reachValue", { defaultValue: "EU · LATAM · Africa" })}
              hint={t("marketing.landing.promises.reachHint", { defaultValue: "Bilingual EN/FR, three regional currencies." })}
              icon={<Users className="h-4 w-4" />}
              status={{ tone: "open", label: t("marketing.landing.promises.statusOpen", { defaultValue: "Open" }) }}
            />
            <StatCard
              label={t("marketing.landing.promises.trustLabel", { defaultValue: "How we keep it honest" })}
              value={t("marketing.landing.promises.trustValue", { defaultValue: "Vetted providers only" })}
              hint={t("marketing.landing.promises.trustHint", { defaultValue: "Every expert KYC'd. Outcomes tracked openly." })}
              icon={<ShieldCheck className="h-4 w-4" />}
              status={{ tone: "beta", label: t("marketing.landing.promises.statusBeta", { defaultValue: "Beta" }) }}
            />
          </div>
        </section>

        {/* Outcome proof bar — hidden 2026-06-19 pending refresh */}
        {false && (
        <section className="container relative pb-16 md:pb-20">
          <div className="mb-8 flex items-end justify-between gap-4">
            <SectionHeader
              eyebrow={t("marketing.landing.proofBar.eyebrow", { defaultValue: "Proof" })}
              title={t("marketing.landing.proofBar.title", { defaultValue: "Real founders. Real outcomes." })}
              lede={t("marketing.landing.proofBar.lede", { defaultValue: "Selected stories from operators we coached this year." })}
            />
            <Link to="/case-studies" className="hidden text-sm font-medium text-primary hover:underline sm:inline-flex">
              {t("marketing.landing.proofBar.viewAll", { defaultValue: "All case studies" })} →
            </Link>
          </div>
          <div className="-mx-4 flex snap-x snap-mandatory gap-4 overflow-x-auto px-4 pb-4 md:mx-0 md:grid md:grid-cols-4 md:gap-6 md:overflow-visible md:px-0 md:pb-0">
            {proofs.map((cs) => {
              const firstName = (cs.founder_name || "").split(" ")[0];
              const kpi = cs.metrics?.kpis?.[0];
              const metric = kpi?.value || "";
              const metricLabel = kpi?.[`label_${lang}`] || kpi?.label_en || "";
              return (
                <Link
                  key={cs.slug}
                  to={`/case-studies/${cs.slug}`}
                  className="group block w-[260px] shrink-0 snap-start overflow-hidden rounded-2xl border border-border bg-card transition-all duration-base ease-brand hover:-translate-y-1 hover:border-primary/60 hover:shadow-glow-primary md:w-auto"
                >
                  <div className="relative aspect-[4/3] overflow-hidden bg-card">
                    {cs.cover_image_url ? (
                      <SmartImage
                        src={cs.cover_image_url}
                        alt={`${cs.company} — ${cs.city}`}
                        className="h-full w-full object-cover transition-transform duration-base group-hover:scale-105"
                      />
                    ) : (
                      <>
                        <CaseStudyCoverArt className="absolute inset-0 h-full w-full transition-transform duration-base group-hover:scale-[1.03]" />
                        <div className="absolute left-3 top-3">
                          <span className="inline-flex items-center rounded-full border border-border/60 bg-background/80 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-foreground backdrop-blur">
                            {cs.city}
                          </span>
                        </div>
                        <div className="absolute inset-x-4 bottom-3">
                          <div className="font-display text-xl font-extrabold leading-tight text-foreground">
                            {cs.company}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                  <div className="p-5">
                    <div className="mb-2 t-display-sm text-foreground">{metric}</div>
                    {metricLabel && (
                      <div className="mb-3 text-xs uppercase tracking-wide text-muted-foreground">{metricLabel}</div>
                    )}
                    <div className="text-sm font-semibold text-foreground">{firstName} · {cs.company}</div>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <MapPin className="h-3 w-3" /> {cs.city}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
        )}

        {/* Pillars band */}
        <section className="container relative pb-20 md:pb-24">
          <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
            <SectionHeader
              eyebrow={tt(EcosystemContent.pillars_band.eyebrow, lang)}
              title={tt((EcosystemContent.pillars_band as any).title, lang)}
              lede={tt((EcosystemContent.pillars_band as any).subtitle, lang)}
            />
            <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground">
              {tt(EcosystemContent.pillars_band.hint, lang)}
            </p>
          </div>
          <div className="grid grid-cols-1 gap-4 min-[420px]:grid-cols-2 md:grid-cols-3 min-[1100px]:grid-cols-3 xl:grid-cols-6">
            {EcosystemContent.pillars.map((p) => {
              const Icon = ICONS[p.icon as keyof typeof ICONS];
              const hue = (p as any).hue ?? 258;
              const priceSku = (p as any).priceSku as string | undefined;
              const priceSuffix = (p as any).priceSuffix as string | undefined;
              const fromLabel = t("common.from", { defaultValue: "From" });
              return (
                <Link
                  key={p.number}
                  to={p.href}
                  className="group relative flex min-h-[280px] flex-col rounded-2xl border border-border bg-card p-7 transition-all duration-base ease-brand hover:-translate-y-1 hover:border-primary/60 hover:shadow-glow-primary"
                >
                  <div className="mb-4 flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[11px] font-semibold tracking-[0.18em] text-muted-foreground">
                        {p.number}
                      </span>
                      <div
                        className="grid h-10 w-10 place-items-center rounded-xl transition-transform duration-base group-hover:scale-110"
                        style={{
                          background: `hsl(${hue} 70% 60% / 0.12)`,
                          color: `hsl(${hue} 70% 50%)`,
                        }}
                      >
                        {Icon ? <Icon className="h-5 w-5" strokeWidth={1.75} /> : null}
                      </div>
                    </div>
                    {priceSku && (
                      <span className="inline-flex items-center gap-1 rounded-full border border-border bg-background/60 px-2.5 py-0.5 text-[11px] font-medium text-foreground backdrop-blur">
                        {fromLabel} <PriceBadge sku={priceSku} />{priceSuffix}
                      </span>
                    )}
                  </div>
                  <h3 className="mb-2 text-foreground" style={type.cardTitle}>
                    {tt(p.title, lang)}
                  </h3>
                  <p className="text-sm leading-snug text-muted-foreground">
                    {tt(p.tagline, lang)}
                  </p>
                  <div className="mt-auto flex items-end justify-between gap-3 pt-5">
                    {(p as any).kpi ? (
                      <span className="inline-flex items-center rounded-full bg-muted px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                        {tt((p as any).kpi, lang)}
                      </span>
                    ) : <span />}
                    <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-primary/40 bg-primary/10 px-3 py-1 text-xs font-medium text-primary transition group-hover:bg-primary group-hover:text-primary-foreground">
                      {t("common.explore", { defaultValue: "Explore" })} <ArrowRight className="h-3 w-3" />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>

        {/* Community CTA band */}
        <section className="container relative pb-20 md:pb-24">
          <div className="relative overflow-hidden rounded-3xl border border-border bg-card p-8 md:p-12">
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 opacity-[0.06]"
              style={{
                backgroundImage: "radial-gradient(hsl(var(--foreground)) 1px, transparent 1px)",
                backgroundSize: "20px 20px",
              }}
            />
            <div className="relative flex flex-col items-start gap-6 md:flex-row md:items-center md:justify-between">
              <div className="flex items-start gap-4">
                <div
                  className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl"
                  style={{ background: "hsl(280 70% 60% / 0.12)", color: "hsl(280 70% 50%)" }}
                >
                  <Users2 className="h-6 w-6" strokeWidth={1.75} />
                </div>
                <div className="space-y-2">
                  <p className="font-mono-jb text-[11px] font-bold uppercase tracking-[0.22em] text-accent">
                    {t("home.community.eyebrow")}
                  </p>
                  <h2 className="text-foreground" style={type.displayMd}>
                    {t("home.community.title")}
                  </h2>
                  <p className="max-w-2xl text-muted-foreground">
                    {t("home.community.sub")}
                  </p>
                </div>
              </div>
              <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
                <Button asChild variant="brand" size="xl" className="group w-full sm:w-auto">
                  <Link to="/signup">
                    {t("home.community.ctaPrimary")}
                    <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                </Button>
                <Button asChild variant="brandOutline" size="xl" className="w-full sm:w-auto">
                  <Link to="/community">{t("home.community.ctaSecondary")}</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Cities band — hidden 2026-06-19 pending refresh */}
      {false && (
      <section className="border-b border-border bg-muted/30">
        <div className="container py-16">
          <div className="mb-10 text-center">
            <h2 className="text-foreground" style={type.displayLg}>{t("marketing.landing.citiesTitle")}</h2>
            <p className="mt-2 text-muted-foreground" style={type.bodyLg}>{t("marketing.landing.citiesSubtitle")}</p>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {cities.map((c) => (
              <Card key={c.name}>
                <CardHeader>
                  <div className="mb-2 flex items-center gap-2 text-primary">
                    <MapPin className="h-4 w-4" />
                    <span className="text-sm font-semibold">{c.name} · {c.country}</span>
                  </div>
                  <CardDescription className="text-base italic">"{c.quote}"</CardDescription>
                  <Link to={`/case-studies/${c.slug}`} className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline">
                    {t("home.readCaseStudy", t("marketing.caseStudies.readStory", { defaultValue: "Read the case study →" }))} <ArrowRight className="h-3 w-3" />
                  </Link>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Featured bootcamps */}
      <section className="border-b border-border bg-muted/30">
        <Reveal className="container py-20 md:py-24">
          <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
            <SectionHeader
              eyebrow={t("marketing.landing.featuredEyebrow", { defaultValue: "Featured cohorts" })}
              title={t("marketing.landing.featuredTitle")}
              lede={t("marketing.landing.featuredSubtitle")}
            />
            <Button asChild variant="ghost" className="hidden sm:inline-flex">
              <Link to="/catalog">{t("marketing.catalog.viewAll")} →</Link>
            </Button>
          </div>
          <StaggerGrid className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {loading
              ? Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-56" />)
              : bootcamps.map((b) => (
                  <Link key={b.id} to={`/catalog/${b.slug}`} className="group block">
                    <Card className={`h-full overflow-hidden transition group-hover:-translate-y-1 group-hover:border-primary/50 group-hover:shadow-lg ${b.is_flagship ? "ring-1 ring-primary/40" : ""}`}>
                      <div className="relative">
                        <BootcampCover slug={b.slug} title={pick(b, "title")} description={pick(b, "subtitle") ?? undefined} size="sm" introVideoSrc={getIntroVideoForSlug(b.slug, i18n.language)} />
                        {b.is_flagship && (
                          <Badge className="absolute left-3 top-3 z-10 border-0 bg-primary px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-primary-foreground hover:bg-primary">
                            ★ Flagship
                          </Badge>
                        )}
                      </div>
                      <CardHeader>
                        <Badge variant="outline" className="mb-3 w-fit capitalize">{b.level}</Badge>
                        <CardTitle className="group-hover:text-primary" style={type.cardTitle}>{pick(b, "title")}</CardTitle>
                        <CardDescription className="line-clamp-2" style={type.bodySm}>{pick(b, "subtitle")}</CardDescription>
                      </CardHeader>
                      <CardContent className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">{b.duration_weeks} {t("marketing.catalog.weeks", { count: b.duration_weeks ?? 0 })}</span>
                        <Price size="inline" eurCents={Math.round(Number(b.price_eur ?? 0) * 100)} />
                      </CardContent>
                    </Card>
                  </Link>
                ))}
          </StaggerGrid>
        </Reveal>
      </section>

      {/* How it works */}
      <section className="border-b border-border">
        <div className="container py-20 md:py-24">
          <SectionHeader
            className="mb-12"
            eyebrow={t("marketing.landing.howItWorks.eyebrow", { defaultValue: "Process" })}
            title={t("marketing.landing.howItWorks.title", { defaultValue: "How it works" })}
            lede={t("marketing.landing.howItWorks.subtitle", { defaultValue: "From diagnostic to credential in four steps." })}
          />
          <Reveal>
          <ol className="relative grid gap-10 md:grid-cols-4 md:gap-6">
            {/* Desktop connector — dashed rail with primary fill that animates in */}
            <div
              aria-hidden
              className="pointer-events-none absolute left-7 right-7 top-7 hidden md:block"
            >
              <div className="h-[2px] w-full bg-[linear-gradient(to_right,hsl(var(--border))_50%,transparent_0)] [background-size:8px_2px]" />
              <div className="absolute left-0 right-0 top-0 h-[2px] rounded-full bg-gradient-to-r from-primary via-primary/80 to-transparent" />
            </div>
            {/* Mobile vertical rail */}
            <div aria-hidden className="pointer-events-none absolute bottom-3 left-7 top-3 w-px bg-border md:hidden" />

            {[
              { n: "01", Icon: Compass, label: t("marketing.landing.howItWorks.s1"), sub: t("marketing.landing.howItWorks.s1Sub") },
              { n: "02", Icon: Map, label: t("marketing.landing.howItWorks.s2"), sub: t("marketing.landing.howItWorks.s2Sub") },
              { n: "03", Icon: Rocket, label: t("marketing.landing.howItWorks.s3"), sub: t("marketing.landing.howItWorks.s3Sub") },
              { n: "04", Icon: BadgeCheck, label: t("marketing.landing.howItWorks.s4"), sub: t("marketing.landing.howItWorks.s4Sub") },
            ].map((step) => {
              const StepIcon = step.Icon;
              return (
                <li key={step.n} className="relative flex gap-5 md:flex-col md:gap-4">
                  <div className="relative z-10 shrink-0">
                    <div
                      className="grid h-14 w-14 place-items-center rounded-2xl border border-primary/30 bg-background text-primary shadow-[inset_0_0_0_1px_hsl(var(--primary)/0.08),0_8px_24px_-12px_hsl(var(--primary)/0.45)] transition-transform duration-base ease-brand hover:-translate-y-0.5"
                      style={{ background: "linear-gradient(180deg, hsl(var(--background)), hsl(var(--primary) / 0.06))" }}
                    >
                      <StepIcon className="h-6 w-6" strokeWidth={1.75} />
                    </div>
                    <span className="absolute -right-1 -top-1 inline-flex items-center justify-center rounded-full border border-border bg-background px-1.5 py-px font-mono text-[10px] font-semibold tracking-wider text-muted-foreground">
                      {step.n}
                    </span>
                  </div>
                  <div className="pt-1.5 md:pt-0">
                    <p className="text-base font-semibold leading-snug text-foreground">
                      {step.label}
                    </p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {step.sub}
                    </p>
                  </div>
                </li>
              );
            })}
          </ol>
          </Reveal>
        </div>
      </section>

      {/* Pricing teaser */}
      <section className="border-b border-border">
        <div className="container py-20 md:py-24">
          <SectionHeader
            className="mb-12"
            align="center"
            eyebrow={t("marketing.landing.pricingEyebrow", { defaultValue: "Pricing" })}
            title={t("marketing.landing.pricingTitle")}
            lede={t("marketing.landing.pricingSubtitle")}
          />
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {teaserTiers.map((tier) => (
              <Card key={tier.key} className={tier.highlight ? "border-primary shadow-lg" : ""}>
                <CardHeader>
                  {tier.highlight && <Badge className="mb-2 w-fit">{t("marketing.pricing.popular")}</Badge>}
                  <CardTitle style={type.cardTitle}>{tier.name}</CardTitle>
                    {tier.key === "founder" ? (
                      <>
                        <div className="mt-2 inline-flex items-center rounded-full border border-border bg-card p-1 text-xs">
                          <button
                            type="button"
                            onClick={() => setFounderAnnual(false)}
                            className={`rounded-full px-2.5 py-0.5 font-medium transition ${!founderAnnual ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}
                          >
                            {t("marketing.pricing.monthly", { defaultValue: "Monthly" })}
                          </button>
                          <button
                            type="button"
                            onClick={() => setFounderAnnual(true)}
                            className={`rounded-full px-2.5 py-0.5 font-medium transition ${founderAnnual ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}
                          >
                            {t("marketing.pricing.annual", { defaultValue: "Annual" })}
                            <span className="ml-1.5 rounded-full bg-success/15 px-1.5 py-0.5 text-[10px] font-semibold text-success">
                              {t("marketing.pricing.savePct", { defaultValue: "Save {{pct}}%", pct: founderSavingsPct })}
                            </span>
                          </button>
                        </div>
                        <Price
                          className="mt-1"
                          size="card"
                          eurCents={Math.round((founderAnnual ? founderAnnualEur : founderMonthlyEur) * 100)}
                          suffix={founderAnnual
                            ? t("marketing.pricing.perYear", { defaultValue: "/yr" })
                            : t("marketing.pricing.perMonth", { defaultValue: "/mo" })}
                        />
                        <Button
                          className="mt-3 w-full"
                          onClick={() => {
                            import("@/lib/payments/checkoutSession").then(({ startPlanCheckout }) =>
                              startPlanCheckout(
                                founderAnnual ? "founder_pass.annual" : "founder_pass.monthly",
                              ),
                            );
                          }}
                        >
                          {t("marketing.pricing.ctaTrial", { defaultValue: "Start 7-day free trial" })}
                        </Button>
                      </>
                    ) : (
                      <Price
                        className="mt-1"
                        size="card"
                        eurCents={Math.round((tier.fallbackEur ?? 0) * 100)}
                        fromPrefix={tier.fromPrefix}
                        suffix={tier.suffix}
                      />
                    )}
                  <CardDescription style={type.bodySm}>{tier.desc}</CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
          <p className="mt-8 text-center text-sm text-muted-foreground">
            {t("marketing.pricing.alsoAvailable", { defaultValue: "Also available:" })}{" "}
            <span className="text-foreground">{t("marketing.pricing.alsoFractional", { defaultValue: "Fractional Executive (from €1,499/mo)" })}</span>
            {" · "}
            <span className="text-foreground">{t("marketing.pricing.alsoMicroservice", { defaultValue: "Microservice (from €499)" })}</span>
            {" · "}
            <Link to="/enterprise" className="text-primary hover:underline">{t("marketing.pricing.alsoTeams", { defaultValue: "Teams →" })}</Link>
          </p>
          <div className="mt-6 flex justify-end">
            <Link to="/pricing" className="text-sm text-primary hover:underline">
              {t("marketing.landing.pricingCta", { defaultValue: "See full pricing" })} →
            </Link>
          </div>
        </div>
      </section>


      {/* FAQ */}
      <section className="border-b border-border">
        <Reveal className="container py-20 md:py-24">
          <div className="mx-auto max-w-3xl">
            <SectionHeader
              className="mb-10"
              align="center"
              eyebrow={t("marketing.landing.faqEyebrow", { defaultValue: "FAQ" })}
              title={t("marketing.faq.seoTitle", { defaultValue: "Frequently asked questions" })}
              lede={t("marketing.landing.faqSubtitle", { defaultValue: "Quick answers. See the full list on our FAQ page." })}
            />
            <Accordion type="single" collapsible className="w-full">
              {faqItems.map((it, i) => (
                <AccordionItem key={i} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left">{it.q}</AccordionTrigger>
                  <AccordionContent>{it.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
            <div className="mt-8 text-center">
              <Button asChild variant="outline">
                <Link to="/faq">{t("marketing.landing.faqCta", { defaultValue: "See all FAQs" })} →</Link>
              </Button>
            </div>
          </div>
        </Reveal>
      </section>

      {/* Founder */}
      <section className="border-b border-border bg-muted/30">
        <div className="container py-20">
          <div className="mx-auto max-w-3xl text-center">
            <Badge variant="secondary" className="mb-4">{t("marketing.landing.founderBadge")}</Badge>
            <h2 className="text-foreground" style={type.displaySm}>{t("marketing.landing.founderTitle")}</h2>
            <p className="mt-6 text-muted-foreground" style={type.bodyLg}>{t("marketing.landing.founderQuote")}</p>
            <p className="mt-4 font-semibold">— Kevin, {t("marketing.landing.founderRole")}</p>
          </div>
        </div>
      </section>
    </PublicShell>
  );
}
